import { Editor } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

function findPlaceholder(editor: Editor, pluginKey: PluginKey, id: object) {
  const decos = pluginKey.getState(editor.state);

  // @ts-ignore
  const found = decos.find(null, null, (spec) => spec.id === id);
  if (!found.length) return null;
  let el = found[0];

  const pos = el.from;

  const placeholder = el.type.toDOM.firstChild;
  const width = placeholder?.width;
  const height = placeholder?.height;

  return { pos, width, height };
}

export async function startUpload(
  editor: Editor,
  pluginKey: PluginKey,
  file: File,
  pos: number,
) {
  const { view } = editor;
  const { uploadFile } = editor.options;

  if (!uploadFile) return;

  // A fresh object to act as the ID for this upload
  const id = {};

  // Replace the selection with a placeholder
  const { tr } = view.state;
  if (!tr.selection.empty) tr.deleteSelection();

  await new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      tr.setMeta(pluginKey, {
        add: {
          id,
          pos,
          src: reader.result,
        },
      });
      view.dispatch(tr);
      resolve(void 0);
    };
  });

  const { _id } = await uploadFile(file);
  const { schema } = view.state;

  const placeholder = findPlaceholder(editor, pluginKey, id);
  if (!placeholder) return;

  const { pos: newPos, width, height } = placeholder;
  // If the content around the placeholder has been deleted, drop
  // the image
  if (newPos == null) return;

  // const { width, height } = await placeholderPromise;

  // Otherwise, insert it at the placeholder's position, and remove
  // the placeholder
  const node = schema.nodes.image.create({ id: _id, width, height });
  const newLine = schema.nodes.paragraph.create();

  const transaction = view.state.tr
    .replaceWith(newPos, newPos, [node, newLine])
    .setMeta(pluginKey, { remove: { id } });

  view.dispatch(transaction);
}

export const createUploadPlugin = ({
  pluginKey,
  editor,
  accept,
}: {
  pluginKey: PluginKey;
  editor: Editor;
  accept: (files: File[]) => File[];
}) => {
  const _a = 1;

  return new Plugin({
    key: pluginKey,
    state: {
      init() {
        return DecorationSet.empty;
      },
      apply(tr, set) {
        set = set.map(tr.mapping, tr.doc);
        // See if the transaction adds or removes any placeholders
        // @ts-ignore
        const action = tr.getMeta(this);
        if (action && action.add) {
          const { id, pos, src } = action.add;

          const placeholder = document.createElement('div');
          placeholder.setAttribute('class', 'img-placeholder');
          const image = document.createElement('img');
          image.src = src;
          placeholder.appendChild(image);
          const deco = Decoration.widget(pos + 1, placeholder, {
            id,
          });
          set = set.add(tr.doc, [deco]);
        } else if (action && action.remove) {
          // @ts-ignore
          set = set.remove(
            // @ts-ignore
            set.find(null, null, (spec) => spec.id === action.remove.id),
          );
        }
        return set;
      },
    },
    props: {
      decorations(state) {
        return this.getState(state);
      },
      handlePaste(_view, event) {
        if (!event.clipboardData?.files?.[0]) return false;

        const [file] = accept(Array.from(event.clipboardData.files));
        if (!file) return false;

        const pos = editor.view.state.selection.from;

        startUpload(editor, pluginKey, file, pos - 1);
        return true;
      },
      handleDrop(view, event) {
        if (!event.dataTransfer?.files?.[0]) return false;

        const [file] = accept(Array.from(event.dataTransfer.files));
        if (!file) return false;

        const coordinates = view.posAtCoords({
          left: event.clientX,
          top: event.clientY,
        });
        if (!coordinates) return false;

        startUpload(editor, pluginKey, file, coordinates.pos);
        return true;
      },
    },
  });
};
